
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";
  import UPDATE_DNU_FLAG from "@/apollo/user/UpdateDNUFlag.gql";
  import GET_USER_PROFILE from "@/apollo/profile/profileDetails/management/GetUserProfile.gql";
  import { NOTIFICATION } from "@/entities/Events";
  import Message from "@/data/notificationsMessages";
  import { defaultErrorNotificationMessage } from "@/data/errorMessages";
  import gql from "graphql-tag";

  @Component({
    components: {},
  })
  export default class ToggleDNUFlag extends Vue {
    @Prop({ type: String, required: true }) readonly contractorUserId!: string;
    @Prop({ type: Boolean, default: false }) readonly isDNUFlagged!: boolean;

    processing: boolean = false;
    localDNUState: boolean | null = null;

    get currentDNUState(): boolean {
      // Use local state if available (after mutation), otherwise use prop
      return this.localDNUState !== null ? this.localDNUState : this.isDNUFlagged;
    }

    @Watch('isDNUFlagged')
    onPropChange(newVal: boolean) {
      // Reset local state when prop changes (e.g., when navigating to different contractor)
      this.localDNUState = null;
    }

    @Watch('contractorUserId')
    onContractorChange() {
      // Reset local state when contractor changes
      this.localDNUState = null;
    }

    async toggleDNUState(): Promise<void> {
      if (this.processing) {
        return;
      }

      const newState = !this.currentDNUState;

      // Immediately update local state for responsive UI
      this.localDNUState = newState;

      // Check if profile exists first
      try {
        const checkProfile = await this.$apollo.query({
          query: GET_USER_PROFILE,
          variables: { id: this.contractorUserId },
          fetchPolicy: 'network-only'
        });
        const profile = checkProfile.data?.user_profile_by_pk;
        
        if (!profile) {
          this.$eventBus.emit(NOTIFICATION, Message.errorMessage('User profile not found'));
          return;
        }
      } catch (error) {
        // Profile check failed, but continue with mutation
      }

      this.processing = true;
      try {
        const result = await this.$apollo.mutate({
          mutation: UPDATE_DNU_FLAG,
          variables: {
            userId: this.contractorUserId,
            DNUFlag: newState,
          },
          update: (cache, { data }) => {
            // Force cache update - this helps ensure the UI reflects the change
            if (data?.update_user_profile_by_pk) {
              try {
                // Try to write the updated profile to cache
                cache.writeFragment({
                  id: `user_profile:${this.contractorUserId}`,
                  fragment: gql`
                    fragment UpdatedDNUFlag on user_profile {
                      DNU_flag
                    }
                  `,
                  data: {
                    DNU_flag: data.update_user_profile_by_pk.DNU_flag,
                    __typename: 'user_profile'
                  }
                });
              } catch (err) {
                console.log('Cache update failed, will rely on refetch:', err);
              }
            }
          }
        });

        if (!result.data?.update_user_profile_by_pk) {
          this.$eventBus.emit(NOTIFICATION, Message.errorMessage('Failed to update DNU flag'));
          return;
        }

        const action = newState ? "added to" : "removed from";
        this.$eventBus.emit(
          NOTIFICATION,
          Message.successMessage(`Resource ${action} Do Not Use list`)
        );

        this.$emit("updated");
        
        // Force a delay then emit updated again to ensure refetch happens
        setTimeout(() => {
          this.$emit("updated");
        }, 100);
      } catch (error) {
        // Reset local state on error
        this.localDNUState = !newState;
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(error.message || defaultErrorNotificationMessage));
      } finally {
        this.processing = false;
      }
    }
  }
